import React from 'react';
import { isString, uniq } from 'lodash';
import { Box, Typography } from '@mui/material';

import { CandidateListingStatusLabel } from '@constants';
import { ICandidateDetails } from '@types';
import {
  bookedSlotAt,
  formatPositionTitle,
  formatSkillLabel,
  normalizeCandidateStatus,
} from '@utils';
import { Row } from './DetailFacts';

const CHIP_PADDING_X = '8px';
const UNSPECIFIED_LOCATION = 'Other';

const SkillChips: React.FC<{ skills: string[] }> = ({ skills }) => (
  <Box display="flex" gap={0.5} flexWrap="wrap">
    {skills.map((skill) => (
      <Box
        key={skill}
        component="span"
        sx={(theme) => ({
          background: theme.palette.highlight.neutral,
          padding: `2px ${CHIP_PADDING_X}`,
          borderRadius: '4px',
          fontSize: '0.8rem',
        })}
      >
        {formatSkillLabel(skill)}
      </Box>
    ))}
  </Box>
);

export const PersonFacts: React.FC<{
  profile: {
    primarySkills: string[] | null;
    secondarySkills: string[] | null;
    seniority: string | null;
    city?: string | null;
    country: string | null;
  };
}> = ({ profile }) => {
  const location = [profile.city, profile.country]
    .filter((part) => part && part !== UNSPECIFIED_LOCATION)
    .join(', ');

  return (
    <>
      {Boolean(
        profile.primarySkills?.length || profile.secondarySkills?.length,
      ) && (
        <Row label="Skills">
          <Box display="flex" flexDirection="column" gap={1}>
            {Boolean(profile.primarySkills?.length) && (
              <SkillChips skills={profile.primarySkills!} />
            )}
            {Boolean(profile.secondarySkills?.length) && (
              <Typography
                variant="body2"
                color="text.secondary"
                ml={CHIP_PADDING_X}
              >
                {profile.secondarySkills!.map(formatSkillLabel).join(', ')}
              </Typography>
            )}
          </Box>
        </Row>
      )}
      {Boolean(profile.seniority) && (
        <Row label="Seniority">
          <Typography variant="body1">{profile.seniority}</Typography>
        </Row>
      )}
      {Boolean(location) && (
        <Row label="Location">
          <Typography variant="body1">{location}</Typography>
        </Row>
      )}
    </>
  );
};

export const ProfileFacts: React.FC<{ data: ICandidateDetails }> = ({
  data,
}) => {
  const profile = data.profile;
  if (!profile) return null;

  const status = normalizeCandidateStatus(data.status);
  const latestRound = data.interview?.at(-1) || null;
  const booked = bookedSlotAt(
    latestRound?.scheduledSlot,
    latestRound?.clientTimezone,
  );
  const appliedFor = uniq(
    (data.positions ?? []).map(formatPositionTitle).filter(Boolean),
  );

  return (
    <Box>
      <PersonFacts profile={profile} />
      {Boolean(profile.englishLevel) && (
        <Row label="English">
          <Typography variant="body1">{profile.englishLevel}</Typography>
        </Row>
      )}
      {Boolean(profile.noticePeriod) && (
        <Row label="Notice period">
          <Typography variant="body1">{profile.noticePeriod}</Typography>
        </Row>
      )}
      {isString(data.billRate?.value) && (
        <Row label="Bill rate">
          <Typography variant="body1">${data.billRate?.value}</Typography>
        </Row>
      )}
      {Boolean(appliedFor.length) && (
        <Row label="Applying for">
          <Typography variant="body1">{appliedFor.join(', ')}</Typography>
        </Row>
      )}
      <Row label="Stage">
        <Typography variant="body1">
          {status ? CandidateListingStatusLabel[status] : data.status}
        </Typography>
        {Boolean(data.closingReason) && (
          <Typography variant="body2" color="text.secondary">
            {data.closingReason}
          </Typography>
        )}
        {Boolean(booked) && (
          <Typography variant="body2" color="text.secondary">
            Interview {booked!.format('MMM DD, YYYY, hh:mm a')}
          </Typography>
        )}
      </Row>
    </Box>
  );
};
